import { createContext, useContext, useRef, useState, useEffect, ReactNode } from "react";
import { Column, ColumnDef, Table } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { ArrowUpDown, ChevronRight, ExternalLink, Folder, Layers, ListFilter, Settings, type LucideIcon } from "lucide-react";
import { DuplicateLocationsIndicator } from "@/components/shared/DuplicateLocationsIndicator";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableDateFilter } from "./data-table-date-filter";
import { DataTableStringFilter } from "./data-table-string-filter";
import { DataTableFacetedFilter } from "./data-table-faceted-filter";
import { ColumnHeaderFilter } from "./data-table-filter-toolbar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMatterJurisdiction, useMatterLocale } from "@/components/contexts/MatterJurisdictionContext";
import { useRegionBundlePaths } from "@/lib/bundleRoutes";
import { formatRegionalDate } from "@/utils/date-utils";
import { maxLengthPolicies } from "@/lib/common";
import { customDateFilter, dateColumnValue, sortingFunctions, highlightText, computeFileType, getSubtleTagStyle } from "./column-utilities";
import { getDocumentIcon } from "@/lib/document-icons";
import { documentNewTabUrl } from "@/lib/documentRoutes";
import { useTagEditor } from "@/context/TagEditorContext";
import { Document } from "@/hooks/useDocuments";
import { DocumentSelection } from "@/types";
import { documentFileName, emailDisplayName, isEmailFileName } from "@/utils/file-utils";

// ============================================================================
// Constants
// ============================================================================

/** Maximum characters to display before truncating file names */
export const FILE_NAME_TRUNCATION_LENGTH = 70;

/** Maximum width classes for content columns */
export const CONTENT_MAX_WIDTH_CLASSES = "max-w-[650px] xl:max-w-[1200px]";

// ============================================================================
// Clamped Summary Component
// ============================================================================

/**
 * Component that displays text with line-clamp and shows a tooltip with full text when clamped.
 * Uses ResizeObserver to detect when text overflows.
 */
const ClampedText = ({
  children,
  fullText,
  lines = 3,
  className = "",
}: {
  children: ReactNode;
  fullText: string;
  lines?: 2 | 3;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkClamping = () => {
      setIsClamped(el.scrollHeight > el.clientHeight);
    };

    checkClamping();

    const resizeObserver = new ResizeObserver(checkClamping);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [fullText]);

  const lineClampClass = lines === 2 ? 'line-clamp-2' : 'line-clamp-3';

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div ref={ref} className={`${lineClampClass} ${className}`}>
            {children}
          </div>
        </TooltipTrigger>
        {isClamped && (
          <TooltipContent className="max-w-md bg-gray-100 text-gray-800 rounded-lg shadow-lg p-3">
            <p className="text-sm whitespace-pre-wrap">{fullText}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

// ============================================================================
// Shared Cell Components
// ============================================================================

/**
 * Configuration for link behavior in cells
 */
export interface LinkConfig {
  slug: string | undefined;
  onDocumentClick: (documentId: string) => void;
}

/**
 * Shared file type cell renderer.
 * Renders file icon with optional link wrapper.
 * Shows duplicate location indicator if document appears in multiple folders.
 * Optionally shows expand chevron for rows with attachments.
 */
export const FileTypeCell = ({
  fileName,
  originalFileType,
  isDraft,
  linkConfig,
  documentId,
  duplicateLocations,
  openInNewTab,
  matterSlug,
  showExpandChevron,
  canExpand,
  isExpanded,
  onToggleExpand,
  bundleSource,
  getDocumentStatusIcons,
  iconOverlay,
  document,
}: {
  fileName: string;
  originalFileType: string;
  isDraft: boolean;
  linkConfig?: LinkConfig;
  documentId: string;
  duplicateLocations?: Array<{ guid: string; display_name: string; folder_name: string }>;
  /** When true, clicking the icon opens the document in a new tab instead of the document panel */
  openInNewTab?: boolean;
  /** Required when openInNewTab is true */
  matterSlug?: string;
  /** When true, shows expand chevron for rows with attachments */
  showExpandChevron?: boolean;
  /** Whether this row can be expanded (has attachments) */
  canExpand?: boolean;
  /** Whether the row is currently expanded */
  isExpanded?: boolean;
  /** Callback to toggle row expansion */
  onToggleExpand?: () => void;
  /** Bundle this file was generated from */
  bundleSource?: { bundle_id: string; title: string } | null;
  /** Consumer-provided status icons to display next to the file icon */
  getDocumentStatusIcons?: (doc: Document) => Array<{ Icon: LucideIcon; className: string; tooltip: string }>;
  /** Consumer-provided overlay rendered on top of the file icon */
  iconOverlay?: ReactNode;
  /** The full document row, needed for getDocumentStatusIcons */
  document?: Document;
}) => {
  const hasAttachments = originalFileType === "Email with attachments";
  const { Icon, colorClass } = getDocumentIcon(fileName, hasAttachments);
  const hasDuplicates = duplicateLocations && duplicateLocations.length > 0;
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const bundlePaths = useRegionBundlePaths();

  const iconElement = <Icon className={`h-5 w-5 ${colorClass}`} />;

  // Open in new tab (for selection mode)
  const newTabContent = openInNewTab && matterSlug ? (
    <div onClick={(e) => e.stopPropagation()}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => {
                const url = document
                  ? documentNewTabUrl(matterSlug, document)
                  : `/matter/${matterSlug}/f/${documentId}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              className="cursor-pointer hover:opacity-70 transition-opacity"
              aria-label="Open document in new tab"
            >
              {iconElement}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Open in new tab
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  ) : null;

  // Link to document panel (for non-selection mode)
  const linkContent = linkConfig?.slug ? (
    <a
      href={document
        ? documentNewTabUrl(linkConfig.slug, document)
        : `/matter/${linkConfig.slug}/f/${documentId}`}
      onClick={(e) => {
        if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          linkConfig.onDocumentClick(documentId);
        }
      }}
      className="cursor-pointer hover:opacity-70 transition-opacity"
    >
      {iconElement}
    </a>
  ) : null;

  const content = newTabContent || linkContent || iconElement;

  return (
    <div className="flex justify-start">
      <div className="flex flex-col items-start">
        <div className="relative">
        <div className="relative flex flex-row items-center gap-1">
          {showExpandChevron && canExpand && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
              }}
              className="absolute -left-5 p-0.5 hover:bg-gray-100 rounded-sm"
              title={isExpanded ? "Collapse attachments" : "Expand to see attachments"}
            >
              <ChevronRight
                className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </button>
          )}
          {content}
          {iconOverlay && (
            <span
              className="absolute -right-1 -top-1"
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              {iconOverlay}
            </span>
          )}
          {hasDuplicates && linkConfig?.slug && (
            <DuplicateLocationsIndicator
              slug={linkConfig.slug}
              locations={duplicateLocations}
              onDocumentClick={linkConfig.onDocumentClick}
            />
          )}
          {bundleSource && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Layers className={`absolute ${hasDuplicates ? 'left-[calc(50%+26px)]' : 'left-[calc(50%+14px)]'} h-3 w-3 text-gray-400 hover:text-gray-600 shrink-0`} />
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={0} className="text-xs">
                  {ui_labels.bundle_singular} generated from: <Link to={bundlePaths.detail} params={{ slug: (matterSlug || linkConfig?.slug) as string, bundleId: bundleSource.bundle_id }} onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">{bundleSource.title}</Link>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {document && getDocumentStatusIcons?.(document).map((icon, i) => {
            // Offset past any absolutely-positioned icons (duplicates, bundleSource)
            const offset = (hasDuplicates && linkConfig?.slug ? 12 : 0) + (bundleSource ? 12 : 0);
            const baseOffset = 14; // base offset from center of file icon
            return (
              <TooltipProvider key={i}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="absolute" style={{ left: `calc(50% + ${baseOffset + offset + i * 16}px)` }} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                      <icon.Icon className={`h-3.5 w-3.5 shrink-0 ${icon.className}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">{icon.tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
          {isDraft && (
            <span className="absolute left-1/2 top-full -translate-x-1/2 whitespace-nowrap text-[11px] font-medium leading-4 text-gray-500">
              DRAFT
            </span>
          )}
        </div>
        {isDraft && <span className="block h-4" aria-hidden="true" />}
      </div>
    </div>
  );
};

/**
 * Shared file name cell renderer.
 * Renders file name with optional link wrapper and search highlighting.
 */
const FileNameCell = ({
  displayName,
  filterValues,
  linkConfig,
  documentId,
  document,
}: {
  displayName: string;
  filterValues: string[] | undefined;
  linkConfig?: LinkConfig;
  documentId: string;
  document: Document;
}) => {
  // Remove file extension and truncate
  const nameWithoutExtension = displayName.replace(/\.[^/.]+$/, "");
  const truncatedName =
    nameWithoutExtension.length > FILE_NAME_TRUNCATION_LENGTH
      ? nameWithoutExtension.substring(0, FILE_NAME_TRUNCATION_LENGTH) + "..."
      : nameWithoutExtension;

  const textElement = highlightText(truncatedName, filterValues);

  if (linkConfig?.slug) {
    return (
      <div className={CONTENT_MAX_WIDTH_CLASSES} title={nameWithoutExtension}>
        <a
          href={documentNewTabUrl(linkConfig.slug, document)}
          onClick={(e) => {
            if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              linkConfig.onDocumentClick(documentId);
            }
          }}
          className="text-left cursor-pointer font-medium hover:underline"
        >
          {textElement}
        </a>
      </div>
    );
  }

  return (
    <div className={CONTENT_MAX_WIDTH_CLASSES} title={nameWithoutExtension}>
      <div className="font-medium">{textElement}</div>
    </div>
  );
};

// ============================================================================
// Shared Column Logic
// ============================================================================

/** Outlook-synced emails are stored under message ids, so they have no meaningful file name. */
const isOutlookSyncedEmail = (document: Document) =>
  document.source === "outlook" && !document.isAttachment && isEmailFileName(document.fileName);

/**
 * Text for the file name column. Emails show their file name, not their
 * subject; Outlook-synced emails show nothing.
 */
export const fileNameColumnText = (document: Document): string =>
  isOutlookSyncedEmail(document) ? "" : documentFileName(document);

/**
 * Shared filter function for file name columns.
 * Matches the text the column shows.
 */
const fileNameFilterFn = (row: any, _columnId: string, filterValue: string[]) => {
  const text = fileNameColumnText(row.original).toLowerCase();
  if (Array.isArray(filterValue)) {
    return filterValue.some((filter) => text.includes(filter.toLowerCase()));
  }
  return false;
};

/**
 * Shared sorting function for file name columns.
 * Sorts by the text the column shows.
 */
const fileNameSortingFn = (rowA: any, rowB: any) => {
  const textA = fileNameColumnText(rowA.original).toLowerCase();
  const textB = fileNameColumnText(rowB.original).toLowerCase();
  return textA.localeCompare(textB);
};

// ============================================================================
// Selection Context for triggering re-renders
// ============================================================================

/**
 * Context for sharing selection state with header checkbox.
 * This allows the SelectAllCheckbox to re-render when selection changes,
 * even when the parent DataTable is memoized and columns are stable.
 */
interface SelectionContextValue {
  documentSelections: Map<string, DocumentSelection>;
  /** Optional set of guids selected implicitly (e.g. via a checked parent folder). */
  implicitlySelectedDocGuids?: Set<string>;
}

export const SelectionStateContext = createContext<SelectionContextValue | null>(null);

/**
 * Cell checkbox component that subscribes to SelectionStateContext
 * so it re-renders immediately when selection changes.
 */
const SelectionCheckbox = ({
  doc,
  isSelected,
  onToggleSelection,
  isSelectionDisabled,
}: {
  doc: Document;
  isSelected: (guid: string) => boolean;
  onToggleSelection: (doc: Document) => void;
  isSelectionDisabled?: (doc: Document) => { disabled: boolean; reason?: string };
}) => {
  // Subscribe to context so we re-render when selections change
  const context = useContext(SelectionStateContext);
  const selected = isSelected(doc.guid);
  const explicitlySelected = context?.documentSelections.has(doc.guid) ?? false;
  const customDisabled = isSelectionDisabled?.(doc);
  // Folder-covered files are selected implicitly. Unlock only an explicit
  // pick so a click cannot add a file the folder already covers.
  const disabled = (customDisabled?.disabled ?? false) && !explicitlySelected;

  return (
    <div className="flex justify-center" title={disabled && customDisabled?.reason ? customDisabled.reason : undefined}>
      <Checkbox
        checked={selected}
        onCheckedChange={() => onToggleSelection(doc)}
        aria-label="Select row"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        disabled={disabled}
        className={disabled ? "opacity-50 cursor-not-allowed" : ""}
      />
    </div>
  );
};

/**
 * Header checkbox component that computes select-all state from table and context.
 * Uses context to trigger re-renders when selection changes.
 * Uses table state to compute which rows are visible (handles pagination correctly).
 */
const SelectAllCheckbox = ({
  table,
  onSelectAllPage,
  isSelectionDisabled,
}: {
  table: Table<Document>;
  onSelectAllPage: (select: boolean) => void;
  isSelectionDisabled?: (doc: Document) => { disabled: boolean; reason?: string };
}) => {
  const context = useContext(SelectionStateContext);

  // Get visible rows from table state (always current, even on pagination change)
  const visibleRows = table.getRowModel().rows;
  // Exclude disabled rows so the checkbox can reach "checked" state when all
  // selectable rows are selected, allowing the user to click to deselect.
  const selectableRows = isSelectionDisabled
    ? visibleRows.filter(row => !isSelectionDisabled(row.original).disabled)
    : visibleRows;
  // Deduplicate guids — multiple rows can share a guid (same document, different file references)
  const selectableGuids = [...new Set(selectableRows.map(row => row.original.guid))];

  // Compute selection state using context (triggers re-render when selection changes)
  const documentSelections = context?.documentSelections ?? new Map();
  const isAllPageSelected = selectableGuids.length > 0 &&
    selectableGuids.every(guid => documentSelections.has(guid));
  const isSomePageSelected = selectableGuids.some(guid => documentSelections.has(guid)) && !isAllPageSelected;

  return (
    <Checkbox
      checked={isAllPageSelected ? true : isSomePageSelected ? "indeterminate" : false}
      onCheckedChange={(checked) => onSelectAllPage(!!checked)}
      aria-label="Select all on page"
      onClick={(e) => e.stopPropagation()}
    />
  );
};

/**
 * Standalone "Manage Tags" button used in the Tags column header when the inline
 * tag filter is moved to the filter toolbar (so tag management stays accessible).
 */
const ManageTagsButton = () => {
  const { openTagEditor } = useTagEditor();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="ml-0.5 h-8 w-6 p-0"
      onClick={openTagEditor}
      title="Manage Tags"
    >
      <Settings className="h-4 w-4" strokeWidth={1.5} />
    </Button>
  );
};

/**
 * Sort toggle shown next to sortable column headers.
 * Inherits the muted header text color (stone-500) with a thin stroke so it
 * reads as a secondary control — matching the Tags/Settings header icon.
 */
export const SortButton = ({ column }: { column: Column<Document, unknown> }) => (
  <Button
    variant="ghost"
    size="sm"
    className="ml-0.5 h-8 w-6 p-0"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    aria-label="Toggle sort"
  >
    <ArrowUpDown className="h-4 w-4" strokeWidth={1.5} />
  </Button>
);

// ============================================================================
// Column Definitions
// ============================================================================

/**
 * Configuration for selection mode columns
 */
export interface SelectionColumnConfig {
  isSelected: (docGuid: string) => boolean;
  onToggleSelection: (doc: Document) => void;
  onSelectAllPage?: (select: boolean) => void;
  /** Matter slug for constructing document URLs */
  matterSlug?: string;
  /** Consumer-provided check for whether selection is disabled */
  isSelectionDisabled?: (doc: Document) => { disabled: boolean; reason?: string };
  /** Consumer-provided status icons to display next to the file icon */
  getDocumentStatusIcons?: (doc: Document) => Array<{ Icon: LucideIcon; className: string; tooltip: string }>;
  /** Consumer-provided overlay rendered on top of the file icon */
  getDocumentIconOverlay?: (doc: Document) => ReactNode;
}

/**
 * Creates a selection checkbox column for document selection mode.
 * Includes select-all functionality.
 */
export const createSelectionColumn = (
  config: SelectionColumnConfig
): ColumnDef<Document> => ({
  id: "id",
  accessorKey: "id",
  header: ({ table }) => {
    if (!config.onSelectAllPage) return null;
    // Use SelectAllCheckbox component which:
    // 1. Gets visible rows from table state (correct on pagination change)
    // 2. Uses SelectionStateContext to trigger re-render on selection change
    return (
      <div className="flex justify-center">
        <SelectAllCheckbox
          table={table}
          onSelectAllPage={config.onSelectAllPage}
          isSelectionDisabled={config.isSelectionDisabled}
        />
      </div>
    );
  },
  cell: ({ row }) => {
    return (
      <SelectionCheckbox
        doc={row.original}
        isSelected={config.isSelected}
        onToggleSelection={config.onToggleSelection}
        isSelectionDisabled={config.isSelectionDisabled}
      />
    );
  },
  enableColumnFilter: true,
  filterFn: (row, id, value) => {
    return value.includes(row.getValue(id));
  },
  sortingFn: "myCustomSorting",
});

/**
 * Creates a file type column with icon display.
 * Uses getDocumentIcon for consistent icon rendering.
 * Uses accessorFn to compute the file type for filtering without mutating row data.
 */
export const createFileTypeColumn = (options?: {
  isSelectionMode?: boolean;
  linkConfig?: LinkConfig;
  /** Matter slug for opening documents in new tab (used in selection mode) */
  matterSlug?: string;
  /** When true, shows expand chevron for rows with attachments */
  showExpandChevron?: boolean;
  /** Consumer-provided status icons to display next to the file icon */
  getDocumentStatusIcons?: (doc: Document) => Array<{ Icon: LucideIcon; className: string; tooltip: string }>;
  /** Consumer-provided overlay rendered on top of the file icon */
  iconOverlay?: (doc: Document) => ReactNode;
}): ColumnDef<Document> => ({
  id: "fileType",
  size: 40,
  // The expand chevron, analysis-set badge, and status icons sit outside the 20px
  // file icon. px-6 keeps the chevron inside the cell; the column stays wide enough
  // for the marks on the right. Correspondence, case files, and the document selector
  // share this column.
  meta: {
    filter: { type: "multi", label: "File type" },
    headerClassName: "px-6",
    cellClassName: "px-6",
  },
  accessorFn: (row) => {
    const fileName = row.fileName || "";
    const originalFileType = row.fileType ? row.fileType[0] : "";
    return computeFileType(fileName, originalFileType);
  },
  header: ({ column, table }) => (
    <div className="flex justify-start">
      {table.options.meta?.filtersInToolbar ? (
        <ColumnHeaderFilter table={table} columnId="fileType" className="justify-start">
          <ListFilter className="h-4 w-4" strokeWidth={1.5} />
        </ColumnHeaderFilter>
      ) : (
        <DataTableFacetedFilter column={column} title="File type" />
      )}
    </div>
  ),
  cell: ({ row }) => {
    const canExpand = row.getCanExpand();
    const isExpanded = row.getIsExpanded();

    return (
      <FileTypeCell
        fileName={row.original.fileName || ""}
        originalFileType={row.original.fileType ? row.original.fileType[0] : ""}
        isDraft={row.original.is_draft ?? false}
        linkConfig={options?.isSelectionMode ? undefined : options?.linkConfig}
        documentId={row.original.id}
        duplicateLocations={row.original.duplicateLocations}
        openInNewTab={options?.isSelectionMode && !!options?.matterSlug}
        matterSlug={options?.matterSlug}
        showExpandChevron={options?.showExpandChevron}
        canExpand={canExpand}
        isExpanded={isExpanded}
        onToggleExpand={() => row.toggleExpanded()}
        bundleSource={row.original.bundleSource}
        getDocumentStatusIcons={options?.getDocumentStatusIcons}
        iconOverlay={options?.iconOverlay?.(row.original)}
        document={row.original}
      />
    );
  },
  filterFn: "arrIncludesSome",
});

/**
 * Creates a date column with sorting and filtering.
 */
function DocumentDateCell({
  date,
}: {
  date: string | Date | null | undefined;
}) {
  const locale = useMatterLocale();
  return (
    <div className="whitespace-nowrap min-w-[90px]">
      {formatRegionalDate(date, locale)}
    </div>
  );
}

export const createDateColumn = (): ColumnDef<Document> => ({
  accessorKey: "date",
  accessorFn: (row) => dateColumnValue(row),
  // TanStack inverts custom sortingFns for `desc`. Empty dates used to compare
  // as "last" in asc and then flip to the top of the default latest-first sort.
  sortUndefined: "last",
  meta: { filter: { type: "date", label: "Date" } },
  header: ({ column, table }) => (
    <div className="flex flex-row items-center gap-0">
      <ColumnHeaderFilter table={table} columnId="date">Date</ColumnHeaderFilter>
      <SortButton column={column} />
      {!table.options.meta?.filtersInToolbar && <DataTableDateFilter column={column} />}
    </div>
  ),
  cell: ({ row }) => (
    <DocumentDateCell date={row.getValue("date")} />
  ),
  filterFn: customDateFilter,
  sortingFn: (rowA, rowB, columnId) => sortingFunctions.dateSorting(rowA, rowB, columnId),
});

/**
 * Creates a file name column with optional link behavior.
 * Use linkConfig to make names clickable (links to document panel).
 */
export const createFileNameColumn = (options?: {
  linkConfig?: LinkConfig;
  /** Column header and filter label. Defaults to "File name". */
  label?: string;
}): ColumnDef<Document> => {
  const label = options?.label ?? "File name";
  return {
    accessorKey: "fileName",
    meta: { filter: { type: "search", label } },
    header: ({ column, table }) => (
      <div className="flex flex-row items-center gap-0">
        <ColumnHeaderFilter table={table} columnId="fileName">{label}</ColumnHeaderFilter>
        <SortButton column={column} />
        {!table.options.meta?.filtersInToolbar && <DataTableStringFilter column={column} title={label} />}
      </div>
    ),
    cell: ({ row, column }) => {
      const text = fileNameColumnText(row.original);
      if (!text) {
        return <div className={`${CONTENT_MAX_WIDTH_CLASSES} text-gray-400`}>-</div>;
      }
      return (
        <FileNameCell
          displayName={text}
          filterValues={column.getFilterValue() as string[] | undefined}
          linkConfig={options?.linkConfig}
          documentId={row.original.id}
          document={row.original}
        />
      );
    },
    filterFn: fileNameFilterFn,
    sortingFn: fileNameSortingFn,
  };
};

/** Subject line shown above an email's summary in case file tables. */
const caseFileSummaryTitle = (document: Document): string =>
  emailDisplayName(document.fileName, document.subject) ?? "";

/**
 * Creates a summary column with text filtering and highlighting.
 * Emails show their subject line above the summary; when linkConfig is
 * provided, the subject opens the document panel.
 */
export const createSummaryColumn = (options?: {
  linkConfig?: LinkConfig;
}): ColumnDef<Document> => ({
  accessorKey: "summary",
  meta: { filter: { type: "search", label: "Summary" } },
  header: ({ column, table }) => (
    <div className="flex flex-row items-center gap-2 min-w-[100px]">
      <ColumnHeaderFilter table={table} columnId="summary">Summary</ColumnHeaderFilter>
      {!table.options.meta?.filtersInToolbar && <DataTableStringFilter column={column} title="Summary" />}
    </div>
  ),
  cell: ({ row, column }) => (
    <SummaryWithTitleCell
      row={row}
      title={caseFileSummaryTitle(row.original)}
      filterValues={column.getFilterValue() as string[] | undefined}
      linkConfig={options?.linkConfig}
    />
  ),
  filterFn: (row, columnId, filterValue) => {
    const summary = row.getValue(columnId);
    if (typeof summary === "string" && Array.isArray(filterValue)) {
      const haystack = `${summary}\n${caseFileSummaryTitle(row.original)}`.toLowerCase();
      return filterValue.some((filter) => haystack.includes(filter.toLowerCase()));
    }
    return false;
  },
});

/**
 * Shared summary cell with an optional title line above the summary.
 * When linkConfig is provided, the title becomes clickable and opens the document panel.
 */
const SummaryWithTitleCell = ({
  row,
  title,
  filterValues,
  linkConfig,
  widthClassName,
  folderPathById,
  deepAnalysisOnly,
}: {
  row: { original: Document; getValue: (key: string) => string };
  title: string;
  filterValues: string[] | undefined;
  linkConfig?: LinkConfig;
  widthClassName?: string;
  folderPathById?: ReadonlyMap<number, string>;
  deepAnalysisOnly?: boolean;
}) => {
  const summary = row.getValue("summary") || '';
  const documentId = row.original.guid;
  const folderId = Number(row.original.document_type?.id);
  const rawFolderPath = Number.isFinite(folderId) ? folderPathById?.get(folderId) : undefined;
  const folderPath = rawFolderPath
    ?.split('/')
    .filter(Boolean)
    .join(' / ');

  const titleContent = linkConfig?.slug ? (
    <Link
      to="/matter/$slug/f/$fileId"
      params={{ slug: linkConfig.slug, fileId: documentId }}
      onClick={(e) => {
        if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          linkConfig.onDocumentClick(documentId);
        }
      }}
      className="text-gray-500 text-xs hover:underline cursor-pointer"
    >
      {highlightText(title, filterValues)}
    </Link>
  ) : (
    <span className="text-gray-500 text-xs cursor-default">
      {highlightText(title, filterValues)}
    </span>
  );

  return (
    <div className={`${CONTENT_MAX_WIDTH_CLASSES} ${widthClassName ?? ''}`}>
      {folderPath && row.original.document_type?.guid && linkConfig?.slug ? (
        <Link
          to="/matter/$slug/case-files/folder/$folderGuid"
          params={{ slug: linkConfig.slug, folderGuid: row.original.document_type.guid }}
          search={deepAnalysisOnly ? { view: 'deep-analysis' } : {}}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          className="mb-0.5 flex min-w-0 max-w-full items-center gap-1 text-[11px] leading-4 text-slate-400 hover:text-slate-600"
          title={`Source folder: ${folderPath}`}
          aria-label={`Open source folder ${folderPath}`}
        >
          <Folder className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{folderPath}</span>
        </Link>
      ) : null}
      {title ? (
        <ClampedText fullText={title} lines={2}>
          {titleContent}
        </ClampedText>
      ) : null}
      <ClampedText fullText={summary}>
        {highlightText(
          summary.substring(0, maxLengthPolicies.summary),
          filterValues
        )}
      </ClampedText>
    </div>
  );
};

/** Subject line for emails, filename for other correspondence documents. */
const correspondenceSummaryTitle = (document: Document): string =>
  emailDisplayName(document.fileName, document.subject) ?? (document.fileName || '');

/**
 * Shared filter function for correspondence summary column.
 * Searches summary, subject, and filename.
 */
const correspondenceSummaryFilterFn = (row: any, columnId: string, filterValue: string[]) => {
  const summary = row.getValue(columnId);
  const subject = row.original.subject || '';
  const fileName = row.original.fileName || '';

  if (typeof summary === 'string' && Array.isArray(filterValue)) {
    const lowerSummary = summary.toLowerCase();
    const lowerSubject = subject.toLowerCase();
    const lowerFileName = fileName.toLowerCase();

    return filterValue.some((filter) => {
      const lowerFilter = filter.toLowerCase();
      return lowerSummary.includes(lowerFilter) || lowerSubject.includes(lowerFilter) || lowerFileName.includes(lowerFilter);
    });
  }
  return false;
};

/**
 * Creates a correspondence-specific summary column with subject/filename title.
 * Shows subject line for emails, filename for other documents, above the summary.
 * When linkConfig is provided, the title becomes clickable and opens the document panel.
 */
export const createCorrespondenceSummaryColumn = (options?: {
  linkConfig?: LinkConfig;
  /** Extra width classes (e.g. min-w-*) applied to the header and cell to widen the column. */
  widthClassName?: string;
  /** Existing folder metadata keyed by id, used to show the source without another table column. */
  folderPathById?: ReadonlyMap<number, string>;
  deepAnalysisOnly?: boolean;
}): ColumnDef<Document> => ({
  accessorKey: "summary",
  meta: { filter: { type: "search", label: "Summary" } },
  header: ({ column, table }) => (
    <div className={`flex flex-row items-center gap-2 ${options?.widthClassName ?? 'min-w-[100px]'}`}>
      <ColumnHeaderFilter table={table} columnId="summary">Summary</ColumnHeaderFilter>
      {!table.options.meta?.filtersInToolbar && <DataTableStringFilter column={column} title="Summary" />}
    </div>
  ),
  cell: ({ row, column }) => (
    <SummaryWithTitleCell
      row={row}
      title={correspondenceSummaryTitle(row.original)}
      filterValues={column.getFilterValue() as string[] | undefined}
      linkConfig={options?.linkConfig}
      widthClassName={options?.widthClassName}
      folderPathById={options?.folderPathById}
      deepAnalysisOnly={options?.deepAnalysisOnly}
    />
  ),
  filterFn: correspondenceSummaryFilterFn,
});

/**
 * Creates a From column for correspondence/email documents.
 */
export const createFromColumn = (): ColumnDef<Document> => ({
  accessorKey: "from",
  meta: { filter: { type: "multi", label: "From" } },
  header: ({ column, table }) => (
    <div className="flex flex-row items-center gap-0">
      <ColumnHeaderFilter table={table} columnId="from">From</ColumnHeaderFilter>
      <SortButton column={column} />
      {!table.options.meta?.filtersInToolbar && <DataTableFacetedFilter column={column} title="From" />}
    </div>
  ),
  cell: ({ row }) => {
    const from = row.getValue("from") as string[] | undefined;
    return (
      <div className="flex flex-row items-center gap-1">
        <div>{from?.join(", ") || ""}</div>
      </div>
    );
  },
  filterFn: "arrIncludesSome",
});

/**
 * Creates a To column for correspondence/email documents.
 */
export const createToColumn = (): ColumnDef<Document> => ({
  accessorKey: "to",
  meta: { filter: { type: "multi", label: "To" } },
  header: ({ column, table }) => (
    <div className="flex flex-row items-center gap-0">
      <ColumnHeaderFilter table={table} columnId="to">To</ColumnHeaderFilter>
      <SortButton column={column} />
      {!table.options.meta?.filtersInToolbar && <DataTableFacetedFilter column={column} title="To" />}
    </div>
  ),
  cell: ({ row }) => {
    const to = row.getValue("to") as string[] | undefined;
    return (
      <div className="flex flex-row items-center gap-1">
        <div>{to?.join(", ") || ""}</div>
      </div>
    );
  },
  filterFn: "arrIncludesSome",
});

/**
 * Creates a standard table checkbox column for row selection.
 * Uses TanStack Table's built-in row selection state.
 */
export const createTableCheckboxColumn = <T extends { id?: string } = Document>(): ColumnDef<T> => ({
  id: "id",
  accessorKey: "id",
  size: 40,
  // Shrink to the checkbox. Without this, an auto-layout table gives the column the spare
  // width when every row is a non-selectable folder (empty cells), which indents the icons.
  meta: {
    headerClassName: "w-px",
    cellClassName: "w-px",
  },
  header: ({ table }) => (
    <div className="flex justify-center">
      <Checkbox
        className="border-stone-300"
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    </div>
  ),
  cell: ({ row, table }) => (
    <div className="flex justify-center">
      {row.getCanSelect() ? (
        <Checkbox
          className="border-stone-300"
          checked={
            row.getIsSelected()
              ? true
              : row.getIsSomeSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => {
            e.stopPropagation();
            if (e.shiftKey) {
              e.preventDefault();
            }
            const meta = table.options.meta as { handleCheckboxClick?: (rowId: string, event: any) => void };
            if (meta?.handleCheckboxClick) {
              meta.handleCheckboxClick(row.id, e);
            }
          }}
          onDoubleClick={(e) => e.stopPropagation()}
        />
      ) : null}
    </div>
  ),
  enableColumnFilter: true,
  filterFn: (row, id, value) => {
    return value.includes(row.getValue(id));
  },
  sortingFn: "myCustomSorting",
});

/**
 * Re-exported from `column-utilities` for backwards compatibility with existing
 * imports (e.g. `AttachmentRow`). The canonical definition now lives alongside
 * the other shared tag color helpers.
 */
export { getSubtleTagStyle };

/**
 * Creates a tags column with color-coded badges.
 */
export const createTagsColumn = (options?: {
  hideSettings?: boolean;
  tagColors?: Record<string, string>;
}): ColumnDef<Document> => ({
  accessorKey: "tags",
  meta: { filter: { type: "multi", label: "Tags" } },
  header: ({ column, table }) => (
    <div className="flex flex-row items-center gap-2">
      <ColumnHeaderFilter table={table} columnId="tags">Tags</ColumnHeaderFilter>
      {table.options.meta?.filtersInToolbar ? (
        !options?.hideSettings && <ManageTagsButton />
      ) : (
        <DataTableFacetedFilter
          column={column}
          title="Tags"
          hideSettings={options?.hideSettings}
          tagColors={options?.tagColors}
        />
      )}
    </div>
  ),
  cell: ({ row }) => (
    <div className="flex flex-wrap gap-1">
      {row.getValue<string[]>("tags")?.map((tag, index) => {
        const hasStoredColor =
          row.original.tag_colors &&
          row.original.tag_colors[tag] &&
          row.original.tag_colors[tag] !== undefined;

        const baseColor = hasStoredColor
          ? row.original.tag_colors![tag]
          : "#CCCCCC";

        return (
          <span
            key={index}
            className="inline-block px-1.5 py-0 font-medium rounded-full text-xs capitalize whitespace-nowrap"
            style={getSubtleTagStyle(baseColor)}
          >
            {tag}
          </span>
        );
      })}
    </div>
  ),
  filterFn: "arrIncludesSome",
});

/**
 * Creates an "open in new tab" action column for selection mode.
 * Shows an external link icon on row hover that opens the document in a new tab.
 */
export const createOpenInNewTabColumn = (matterSlug?: string): ColumnDef<Document> => ({
  id: "openInNewTab",
  enableHiding: false,
  size: 40,
  header: () => null,
  cell: ({ row }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (matterSlug && row.original.id) {
        window.open(documentNewTabUrl(matterSlug, row.original), '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <button
        onClick={handleClick}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded-sm transition-opacity"
        aria-label="Open in new tab"
        title="Open in new tab"
      >
        <ExternalLink className="h-4 w-4 text-gray-500 hover:text-gray-700" />
      </button>
    );
  },
});
